/**
 * EventsPanel
 *
 * Allows users to add upcoming holidays, events, and promotions
 * that should be incorporated into the content plan.
 */

import React, { useState, useEffect } from 'react';
import {
    CalendarDays,
    Gift,
    PartyPopper,
    Megaphone,
    Plus,
    X,
    Check,
    AlertCircle,
    Sparkles
} from 'lucide-react';

// Major holidays by region (can be expanded)
const HOLIDAY_DATABASE = {
    global: [
        { name: "New Year's Day", date: '01-01', type: 'holiday' },
        { name: "Valentine's Day", date: '02-14', type: 'holiday' },
        { name: "International Women's Day", date: '03-08', type: 'holiday' },
        { name: "Earth Day", date: '04-22', type: 'holiday' },
        { name: "Mother's Day", date: '05-second-sunday', type: 'holiday' },
        { name: "Father's Day", date: '06-third-sunday', type: 'holiday' },
        { name: "Halloween", date: '10-31', type: 'holiday' },
        { name: "Black Friday", date: '11-fourth-friday', type: 'shopping' },
        { name: "Cyber Monday", date: '11-cyber-monday', type: 'shopping' },
        { name: "Christmas Eve", date: '12-24', type: 'holiday' },
        { name: "Christmas Day", date: '12-25', type: 'holiday' },
        { name: "New Year's Eve", date: '12-31', type: 'holiday' }
    ],
    us: [
        { name: "Martin Luther King Jr. Day", date: '01-third-monday', type: 'holiday' },
        { name: "Presidents Day", date: '02-third-monday', type: 'holiday' },
        { name: "Memorial Day", date: '05-last-monday', type: 'holiday' },
        { name: "Independence Day", date: '07-04', type: 'holiday' },
        { name: "Labor Day", date: '09-first-monday', type: 'holiday' },
        { name: "Thanksgiving", date: '11-fourth-thursday', type: 'holiday' },
        { name: "Veterans Day", date: '11-11', type: 'holiday' }
    ],
    uk: [
        { name: "Boxing Day", date: '12-26', type: 'holiday' },
        { name: "Guy Fawkes Night", date: '11-05', type: 'holiday' }
    ],
    christian: [
        { name: "Easter Sunday", date: 'easter', type: 'holiday' },
        { name: "Good Friday", date: 'good-friday', type: 'holiday' }
    ]
};

// Calculate Easter date for a given year (Computus algorithm)
function getEasterDate(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

// Get nth weekday of month
function getNthWeekdayOfMonth(year, month, weekday, n) {
    const firstDay = new Date(year, month, 1);
    const firstWeekday = firstDay.getDay();
    let day = 1 + ((weekday - firstWeekday + 7) % 7);
    if (n === 'last') {
        const lastDay = new Date(year, month + 1, 0).getDate();
        while (day + 7 <= lastDay) day += 7;
    } else {
        day += (n - 1) * 7;
    }
    return new Date(year, month, day);
}

// Parse holiday date pattern for a specific month/year
function getHolidayDate(pattern, year, targetMonth) {
    if (pattern === 'easter') {
        return getEasterDate(year);
    }
    if (pattern === 'good-friday') {
        const easter = getEasterDate(year);
        return new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000);
    }

    const parts = pattern.split('-');
    const month = parseInt(parts[0]) - 1;

    if (month !== targetMonth) return null;

    if (parts.length === 2 && !isNaN(parseInt(parts[1]))) {
        return new Date(year, month, parseInt(parts[1]));
    }

    // Handle patterns like "second-sunday", "third-monday", "last-monday"
    if (parts.length === 3) {
        const ordinal = parts[1];
        const dayName = parts[2];
        const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
        const weekday = dayMap[dayName];

        if (ordinal === 'first') return getNthWeekdayOfMonth(year, month, weekday, 1);
        if (ordinal === 'second') return getNthWeekdayOfMonth(year, month, weekday, 2);
        if (ordinal === 'third') return getNthWeekdayOfMonth(year, month, weekday, 3);
        if (ordinal === 'fourth') return getNthWeekdayOfMonth(year, month, weekday, 4);
        if (ordinal === 'last') return getNthWeekdayOfMonth(year, month, weekday, 'last');
    }

    // Handle Cyber Monday (Monday after Thanksgiving)
    if (pattern === '11-cyber-monday' && targetMonth === 10) {
        const thanksgiving = getNthWeekdayOfMonth(year, 10, 4, 4); // 4th Thursday of November
        return new Date(thanksgiving.getTime() + 4 * 24 * 60 * 60 * 1000);
    }

    return null;
}

// Get holidays for a specific month
function getHolidaysForMonth(year, month, regions = ['global', 'us', 'christian']) {
    const holidays = [];

    regions.forEach(region => {
        const regionHolidays = HOLIDAY_DATABASE[region] || [];
        regionHolidays.forEach(holiday => {
            const date = getHolidayDate(holiday.date, year, month);
            if (date && date.getMonth() === month) {
                holidays.push({
                    ...holiday,
                    date: date,
                    dateString: date.toISOString().split('T')[0]
                });
            }
        });
    });

    // Also check Easter which can be in March or April
    if (month === 2 || month === 3) {
        const easter = getEasterDate(year);
        if (easter.getMonth() === month) {
            holidays.push({
                name: 'Easter Sunday',
                date: easter,
                dateString: easter.toISOString().split('T')[0],
                type: 'holiday'
            });
            const goodFriday = new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000);
            if (goodFriday.getMonth() === month) {
                holidays.push({
                    name: 'Good Friday',
                    date: goodFriday,
                    dateString: goodFriday.toISOString().split('T')[0],
                    type: 'holiday'
                });
            }
        }
    }

    return holidays.sort((a, b) => a.date - b.date);
}

const EVENT_TYPES = [
    { id: 'holiday', label: 'Holiday', icon: PartyPopper, color: 'var(--color-info)' },
    { id: 'promotion', label: 'Promotion', icon: Megaphone, color: 'var(--color-success)' },
    { id: 'event', label: 'Event', icon: CalendarDays, color: 'var(--color-warning)' },
    { id: 'launch', label: 'Launch', icon: Gift, color: 'var(--color-error)' }
];

const EventsPanel = ({ startDate, endDate, selectedEvents = [], onEventsChange }) => {
    const [detectedHolidays, setDetectedHolidays] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newEvent, setNewEvent] = useState({ name: '', date: '', type: 'event', description: '' });

    // Format date range for display
    const dateRangeLabel = React.useMemo(() => {
        if (!startDate || !endDate) return 'selected period';
        const start = new Date(startDate);
        const end = new Date(endDate);
        const startMonth = start.toLocaleString('default', { month: 'short' });
        const endMonth = end.toLocaleString('default', { month: 'short' });
        if (startMonth === endMonth && start.getFullYear() === end.getFullYear()) {
            return startMonth;
        }
        return `${startMonth} - ${endMonth}`;
    }, [startDate, endDate]);

    // Detect holidays for the selected date range
    useEffect(() => {
        if (!startDate || !endDate) return;

        const start = new Date(startDate);
        const end = new Date(endDate);
        const holidays = [];
        const seen = new Set();

        // Iterate through all months in range
        let current = new Date(start.getFullYear(), start.getMonth(), 1);
        const endMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0);

        while (current <= endMonth) {
            const monthHolidays = getHolidaysForMonth(current.getFullYear(), current.getMonth());
            monthHolidays.forEach(holiday => {
                // Only include if within date range
                const holidayDate = new Date(holiday.date);
                const key = `${holiday.name}_${holiday.dateString}`;
                if (holidayDate >= start && holidayDate <= end && !seen.has(key)) {
                    holidays.push(holiday);
                    seen.add(key);
                }
            });
            current.setMonth(current.getMonth() + 1);
        }

        setDetectedHolidays(holidays.sort((a, b) => new Date(a.date) - new Date(b.date)));
    }, [startDate, endDate]);

    const handleToggleHoliday = (holiday) => {
        const isSelected = selectedEvents.some(e => e.dateString === holiday.dateString && e.name === holiday.name);

        if (isSelected) {
            onEventsChange(selectedEvents.filter(e => !(e.dateString === holiday.dateString && e.name === holiday.name)));
        } else {
            onEventsChange([...selectedEvents, {
                ...holiday,
                includeInBriefs: true,
                briefCount: 1
            }]);
        }
    };

    const handleAddCustomEvent = () => {
        if (!newEvent.name || !newEvent.date) return;

        const event = {
            ...newEvent,
            dateString: newEvent.date,
            date: new Date(newEvent.date),
            isCustom: true,
            includeInBriefs: true,
            briefCount: 1
        };

        onEventsChange([...selectedEvents, event]);
        setNewEvent({ name: '', date: '', type: 'event', description: '' });
        setShowAddForm(false);
    };

    const handleRemoveEvent = (eventToRemove) => {
        onEventsChange(selectedEvents.filter(e =>
            !(e.dateString === eventToRemove.dateString && e.name === eventToRemove.name)
        ));
    };

    const handleUpdateEventBriefCount = (event, count) => {
        onEventsChange(selectedEvents.map(e =>
            e.dateString === event.dateString && e.name === event.name
                ? { ...e, briefCount: Math.max(1, count) }
                : e
        ));
    };

    return (
        <div className="card" style={{ padding: 'var(--space-4)' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-4)'
            }}>
                <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <CalendarDays size={20} />
                    Events & Promotions
                </h4>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="btn btn-ghost btn-sm"
                >
                    <Plus size={14} />
                    Add Custom
                </button>
            </div>

            {/* Detected Holidays */}
            {detectedHolidays.length > 0 && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-2)'
                    }}>
                        <Sparkles size={14} style={{ color: 'var(--color-primary)' }} />
                        <span style={{
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 500,
                            color: 'var(--color-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Detected in {dateRangeLabel}
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                        {detectedHolidays.map((holiday, idx) => {
                            const isSelected = selectedEvents.some(
                                e => e.dateString === holiday.dateString && e.name === holiday.name
                            );

                            return (
                                <button
                                    key={idx}
                                    onClick={() => handleToggleHoliday(holiday)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: isSelected ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                                        color: isSelected ? 'white' : 'var(--color-text-primary)',
                                        border: `1px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                        borderRadius: 'var(--radius-full)',
                                        cursor: 'pointer',
                                        fontSize: 'var(--font-size-sm)'
                                    }}
                                >
                                    {isSelected && <Check size={14} />}
                                    <PartyPopper size={14} />
                                    {holiday.name}
                                    <span style={{
                                        fontSize: 'var(--font-size-xs)',
                                        opacity: 0.7
                                    }}>
                                        {holiday.date.getDate()}/{holiday.date.getMonth() + 1}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {detectedHolidays.length === 0 && (
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-4)'
                }}>
                    No major holidays detected in {dateRangeLabel}. Add custom events below.
                </div>
            )}

            {/* Selected Events List */}
            {selectedEvents.length > 0 && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                    <div style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500,
                        color: 'var(--color-text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 'var(--space-2)'
                    }}>
                        Selected Events ({selectedEvents.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {selectedEvents.map((event, idx) => {
                            const eventType = EVENT_TYPES.find(t => t.id === event.type) || EVENT_TYPES[0];
                            const Icon = eventType.icon;

                            return (
                                <div
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-3)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                        borderLeft: `3px solid ${eventType.color}`
                                    }}
                                >
                                    <Icon size={16} style={{ color: eventType.color }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 500 }}>{event.name}</div>
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            {new Date(event.date).toLocaleDateString()}
                                            {event.description && ` · ${event.description}`}
                                        </div>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)'
                                    }}>
                                        <label style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            Briefs:
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="5"
                                            value={event.briefCount || 1}
                                            onChange={(e) => handleUpdateEventBriefCount(event, parseInt(e.target.value))}
                                            className="input"
                                            style={{ width: '50px', padding: 'var(--space-1)', textAlign: 'center' }}
                                        />
                                    </div>
                                    <button
                                        onClick={() => handleRemoveEvent(event)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--color-text-tertiary)',
                                            padding: 'var(--space-1)'
                                        }}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Add Custom Event Form */}
            {showAddForm && (
                <div style={{
                    padding: 'var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-3)'
                    }}>
                        <h5 style={{ margin: 0 }}>Add Custom Event</h5>
                        <button
                            onClick={() => setShowAddForm(false)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    Event Name
                                </label>
                                <input
                                    type="text"
                                    value={newEvent.name}
                                    onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                                    placeholder="e.g., Summer Sale, Product Launch"
                                    className="input"
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    Date
                                </label>
                                <input
                                    type="date"
                                    value={newEvent.date}
                                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                                    className="input"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-1)'
                            }}>
                                Type
                            </label>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                {EVENT_TYPES.map(type => {
                                    const Icon = type.icon;
                                    return (
                                        <button
                                            key={type.id}
                                            onClick={() => setNewEvent({ ...newEvent, type: type.id })}
                                            className={`btn btn-sm ${newEvent.type === type.id ? 'btn-primary' : 'btn-ghost'}`}
                                        >
                                            <Icon size={14} />
                                            {type.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-1)'
                            }}>
                                Description (optional)
                            </label>
                            <input
                                type="text"
                                value={newEvent.description}
                                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                                placeholder="e.g., 30% off all products"
                                className="input"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                            <button onClick={() => setShowAddForm(false)} className="btn btn-ghost">
                                Cancel
                            </button>
                            <button
                                onClick={handleAddCustomEvent}
                                disabled={!newEvent.name || !newEvent.date}
                                className="btn btn-primary"
                            >
                                <Plus size={14} />
                                Add Event
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Note */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--space-4)',
                border: '1px solid var(--color-border)'
            }}>
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--color-info)' }} />
                <span>
                    Selected events will be incorporated into AI-generated briefs with themed content
                    and appropriate timing. You can specify how many briefs to create for each event.
                </span>
            </div>
        </div>
    );
};

export default EventsPanel;
